import {
    CloudStorage,
    CloudStorageError,
    CloudStorageErrorCode,
    CloudStorageScope
} from 'react-native-cloud-storage';

import { delay } from '../../../../utils/fn';
import { getRandomNumberString } from '../../../../utils/strings';
import { toLog } from '../Backup';

export const ICLOUD_SCOPE = CloudStorageScope.AppData;

export const extractFileName = (fileName, downloadedOnly) => {
    const parts = fileName?.split?.('/')
        ?.pop
        ?.()
        ?.split
        ?.('.');
    const lastPart = parts?.[parts?.length - 1] || '';
    const prevPart = parts?.[parts?.length - 2] || '';
    if (downloadedOnly) {
        if (lastPart?.endsWith?.('.icloud')) {
            return prevPart || '';
        }
        return lastPart;
    }

    return lastPart?.endsWith?.('.icloud') ? parts?.[parts?.length - 3] ?? '' : prevPart;
};

export const extractFileExtension = (fileName, downloadedOnly = true) => {
    const parts = fileName?.split?.('/')
        ?.pop
        ?.()
        ?.split
        ?.('.');
    const lastPart = parts?.[parts?.length - 1] || '';
    if (downloadedOnly) {
        return lastPart;
    }

    return lastPart?.endsWith?.('.icloud') ? parts?.[parts?.length - 2] ?? '' : lastPart;

};

/**
 * Generate a file name for iCloud based on the profile ID, type, and name.
 * for type = metadata, the device ID is not included.
 * for type = backup, a random string is added to the file name.
 * for type = attach, the name is included in the file name.
 * The file name will be in the format: type_profileId_deviceId_add_attach.extension
 * @param profileId
 * @param type
 * @param name
 * @param deviceId
 * @returns {string}
 */
export const getICloudFileName = ({
    profileId,
    type = 'metadata',
    name = '',
    deviceId: id,
}) => {
    const extension = name ? extractFileExtension(name) : 'json';
    const add = type === 'backup' ? `_${getRandomNumberString(16)}` : '';
    const deviceId = type === 'metadata' ? '' : id;
    const device = type === 'metadata' || type === 'attach' ? '' : `_${deviceId ?? 'unknownID'}`;
    const attach = type === 'attach' ? `_${extractFileName(name)}` : '';
    const result = `${type}_${profileId}${device}${add}${attach}.${extension}`;
    toLog('ICloud:filename', {
        result,
        type,
        name,
        deviceId,
    });
    return result;
};

export const decodeAttach = (fileName, profileId) => fileName?.replace?.(`attach_${profileId}_`, '');
export const isMetadataFile = (fileName, profileId, downloadedOnly = true) => {
    const fromDownloaded = fileName?.startsWith(`metadata_${profileId}`) && fileName?.endsWith('.json');
    if (downloadedOnly) {
        return fromDownloaded;
    }
    return fromDownloaded || ((fileName?.startsWith(`.metadata_${profileId}`) || fileName?.startsWith(`metadata_${profileId}`)) && fileName?.endsWith('.json.icloud'));
};

export const isBackupFile = (fileName, profileId, downloadedOnly = true) => {
    const fromDownloaded = fileName?.startsWith(`backup_${profileId}`) && fileName?.endsWith('.json');
    if (downloadedOnly) {
        return fromDownloaded;
    }
    return fromDownloaded || ((fileName?.startsWith(`.backup_${profileId}`) || fileName?.startsWith(`backup_${profileId}`)) && fileName?.endsWith('.json.icloud'));
};

export const isAttachFile = (fileName, profileId, downloadedOnly = true) => {
    const fromDownloaded = fileName?.startsWith(`attach_${profileId}`) &&
        (fileName?.endsWith('.jpeg') ||
            fileName?.endsWith('.jpg') ||
            fileName?.endsWith('.png') ||
            fileName?.endsWith('.pdf'));
    if (downloadedOnly) {
        return fromDownloaded;
    }

    return fromDownloaded || ((fileName?.startsWith(`.attach_${profileId}`) || fileName?.startsWith(`attach_${profileId}`)) &&
        (fileName?.endsWith('.jpeg.icloud') ||
            fileName?.endsWith('.jpg.icloud') ||
            fileName?.endsWith('.png.icloud') ||
            fileName?.endsWith('.pdf.icloud')));

};

export const isProfileFile = (fileName, profileId, downloadedOnly) => {
    const isAttach = isAttachFile(fileName, profileId, downloadedOnly);
    const isBackup = isBackupFile(fileName, profileId, downloadedOnly);
    const isMetadata = isMetadataFile(fileName, profileId, downloadedOnly);
    // console.log(`isProfileFile ${fileName}`, {
    //     fileName,
    //     profileId,
    //     isBackup,
    //     isMetadata,
    //     isAttach,
    // });
    return isMetadata || isAttach || isBackup;
};

/**
 * Get the list of files in iCloud AppData scope.
 * This function retrieves the list of files stored in the iCloud AppData scope.
 * It uses the CloudStorage.readdir method to read the files from the root directory ('/').
 * If the operation is successful, it returns an array of file names.
 * If the directory is not found (ERR_DIRECTORY_NOT_FOUND), it tries to create it first.
 * @returns {Promise<string[]|*[]>}
 */

export const throwNoIcloud = () => {
    throw new Error(Object.R('errors.iCloudNotAccessible'));
};

export const getFileList = async () => {
    try {
        return await CloudStorage.readdir('/', ICLOUD_SCOPE);
    } catch (e) {
        toLog('ICloud getFileList error', e);

        // Handle the case when the AppData directory doesn't exist yet
        if (e instanceof CloudStorageError && e.code === CloudStorageErrorCode.DIRECTORY_NOT_FOUND) {
            toLog('ICloud getFileList: AppData directory not found, trying to initialize...');

            // First, check if iCloud is available at all
            try {
                const isAvailable = await CloudStorage.isCloudAvailable();
                if (!isAvailable) {
                    toLog('ICloud getFileList: iCloud is not available on this device');
                    throwNoIcloud();
                    return [];
                }
            } catch (availabilityError) {
                toLog('ICloud getFileList: Failed to check iCloud availability', availabilityError);
                throwNoIcloud();
                return [];
            }

            try {
                // Try to create a simple file to initialize the AppData directory
                const tempFileName = `init_${Date.now()}.tmp`;
                await CloudStorage.writeFile(tempFileName, 'init', ICLOUD_SCOPE);
                toLog('ICloud getFileList: Temporary file created successfully');

                // Try to read the directory after creating the file
                let files = [];
                try {
                    files = await CloudStorage.readdir('/', ICLOUD_SCOPE);
                    toLog('ICloud getFileList: Directory read successfully after initialization', { files });
                } catch (readdirError) {
                    toLog('ICloud getFileList: Still cannot read directory after file creation', readdirError);
                    throwNoIcloud();
                    return [];
                }

                // Try to remove the temporary file (optional, don't fail if this doesn't work)
                try {
                    await CloudStorage.unlink(tempFileName, ICLOUD_SCOPE);
                    toLog('ICloud unlink getFileList: Temporary file removed successfully', tempFileName);
                } catch (unlinkError) {
                    toLog('ICloud getFileList: Failed to remove temp file (non-critical)', unlinkError);
                    throwNoIcloud();
                }

                return files;
            } catch (initError) {
                toLog('ICloud getFileList: Failed to initialize AppData directory', initError);

                // Additional logging for debugging
                if (initError instanceof CloudStorageError) {
                    toLog('ICloud getFileList: Init error details', {
                        code: initError.code,
                        message: initError.message,
                        stack: initError.stack,
                    });
                }
                throwNoIcloud();

                return [];
            }
        }

        // Handle other types of errors
        if (e instanceof CloudStorageError) {
            toLog('ICloud getFileList: CloudStorageError details', {
                code: e.code,
                message: e.message,
            });
        }
        throwNoIcloud();
        return [];
    }
};

/**
 * Check if the file name ends with '.icloud'.
 * This function checks if the provided file name ends with '.icloud'.
 * It returns true if the file name ends with '.icloud', otherwise false.
 * @param fileName
 * @returns {boolean|boolean}
 */
export const isFileNameInCloud = fileName => fileName?.endsWith?.('.icloud') ?? false;

/**
 * Extract the file name from a cloud file name.
 * This function checks if the provided file name is a cloud file name (ends with '.icloud').
 * If it is, it removes the last 7 characters ('.icloud') from the file name.
 * If the extracted name starts with a dot, it removes the dot and returns the rest of the name.
 * If the file name is not a cloud file name, it returns the original file name.
 * If the file name is empty or undefined, it returns an empty string.
 * @param fileName
 * @returns {string|undefined|*|string}
 */
export const extractFilenameFromCloudFileName = (fileName) => {
    if (!fileName) {
        return '';
    }
    if (isFileNameInCloud(fileName)) {
        const extractedName = fileName?.substring(0, fileName?.length - 7);
        if (extractedName?.startsWith?.('.')) {
            return extractedName?.substring(1);
        }
        return extractedName;
    }
    return fileName;
};

export const extractFileNameWithoutDevice = (fileName = '') => {
    const nameArray = fileName?.split?.('.');
    if (nameArray?.length < 1) {
        toLog('extractFileNameWithoutDevice:noFile?', fileName);
        return fileName;
    }
    const name = nameArray?.[0];
    const ext = nameArray?.[1];
    const nameParts = name?.split?.('_');
    let newFileName = `${nameParts?.join('_')}.${ext}`;
    if (isBackupFile(fileName, nameParts?.[1])) {
        newFileName = `${nameParts?.[0]}_${nameParts?.[1]}_${nameParts?.[3]}.${ext}`;
    }
    // toLog('extractFileNameWithoutDevice"newFileName', newFileName);
    return newFileName;
};

/**
 * Compare two file names.
 * This function compares two file names, optionally ignoring the '.icloud' suffix.
 * If ignoreCloud is true, it extracts the file names from both cloud file names and compares them.
 * If ignoreCloud is false, it compares the file names directly.
 * It returns true if the file names are equal, otherwise false.
 * @param {Object} params - The parameters for the function.
 * @param fileName1
 * @param fileName2
 * @param ignoreCloud
 * @returns {boolean}
 */
export const compareFileName = ({
    fileName1,
    fileName2,
    ignoreCloud = true,
    ignoreDevice = false,
}) => {
    if (ignoreCloud) {
        let extractedFileName1 = extractFilenameFromCloudFileName(fileName1);
        let extractedFileName2 = extractFilenameFromCloudFileName(fileName2);
        if (ignoreDevice) {
            extractedFileName1 = extractFileNameWithoutDevice(fileName1);
            extractedFileName2 = extractFileNameWithoutDevice(fileName2);
        }
        toLog('compareFileName:ignoreCloud', {
            extractedFileName1,
            extractedFileName2,
            fileName1,
            fileName2,
        });
        return extractedFileName1 === extractedFileName2;
    }
    toLog('compareFileName', {
        fileName1,
        fileName2,
    });
    return fileName1 === fileName2;
};

/**
 * Check if a file is downloaded in iCloud.
 * This function checks if a file with the given fileName is downloaded in iCloud.
 * It retrieves the list of files from iCloud and checks if the file name exists in the list.
 * If the file name is a cloud file name, it extracts the actual file name before checking.
 * It returns a promise that resolves to true if the file is downloaded, otherwise false.
 * If the list is provided, it will use that list, otherwise it will read the files
 * from the iCloud AppData scope.
 * The function uses the getFileList function to retrieve the list of files.
 * It checks if the file name exists in the list, considering both the original file name and
 * the extracted file name from a cloud file name.
 * @param fileName
 * @param list
 * @returns {Promise<boolean>}
 */
export const isFileDownloaded = async (fileName, list) => {
    const fileList = list || (await getFileList());
    const name = isFileNameInCloud(fileName) ? extractFilenameFromCloudFileName(fileName) : fileName;
    return !!fileList?.find(f => f === name);
};

/**
 * Check if a file exists in iCloud.
 * This function checks if a file with the given fileName exists in iCloud.
 * It retrieves the list of files from iCloud and checks if the file name exists in the list.
 * If the file name is a cloud file name, it extracts the actual file name before checking.
 * It returns a promise that resolves to true if the file exists, otherwise false.
 * If the list is provided, it will use that list, otherwise it will read the files
 * from the iCloud AppData scope.
 * The function uses the getFileList function to retrieve the list of files.
 * It checks if the file name exists in the list, considering both the original file name and
 * the extracted file name from a cloud file name.
 * If the file name is empty or undefined, it returns false.
 * If the file name is not provided, it returns false;
 * If the file name is a cloud file name, it extracts the actual file name before checking.
 * If the file name is found in the list, it returns true.
 * If the file name is not found in the list, it checks if the extracted file name
 * from a cloud file name exists in the list and returns true if found, otherwise false.
 * If the file name is not found in the list, it returns false.
 * @param fileName
 * @param list
 * @returns {Promise<boolean>}
 */
export const isFileExists = async (fileName, list) => {
    const fileList = list || (await getFileList());
    const name = isFileNameInCloud(fileName) ? extractFilenameFromCloudFileName(fileName) : fileName;
    const found = !!fileList?.find(f => f === name || f?.endsWith(name));
    if (found) {
        return true;
    }
    return !!fileList?.find(f => extractFilenameFromCloudFileName(f) === name);
};

/**
 * Get file from iCloud.
 * If the file is not downloaded, it will try to download it.
 * If the file is not found, it will return an object with exists: false.
 * If the file is found and downloaded, it will return an object with exists: true,
 * downloaded: true, actualName: fileName, and data: file data if withData is true.
 * If the file is found but not downloaded, it will return an object with exists: true,
 * downloaded: false, actualName: fileName, and data: null.
 * @param fileName
 * @param list
 * @param withData
 * @param tryToDownload
 * @param attempts
 * @param timeout
 * @param defaultData
 * @returns {Promise<{exists: boolean, downloaded: boolean, actualName: (string|*|string), data: null}>}
 */
export const getFileFromCloud = async ({
    fileName,
    list,
    withData = false,
    tryToDownload = true,
    attempts = 60,
    timeout = 2000,
    defaultData = null,
} = {}) => {
    const result = {
        exists: false,
        downloaded: false,
        actualName: extractFilenameFromCloudFileName(fileName),
        data: defaultData,
    };
    if (!fileName) {
        toLog('ICloud getFileFromCloud: no fileName provided');
        return result;
    }
    const fileList = list || (await getFileList());
    if (!(await isFileExists(fileName, fileList))) {
        return result;
    }
    if (await isFileDownloaded(fileName, fileList)) {
        result.exists = true;
        result.downloaded = true;
        if (withData) {
            result.data = await CloudStorage.readFile(extractFilenameFromCloudFileName(fileName), ICLOUD_SCOPE);
        }
        return result;
    }
    if (tryToDownload) {
        toLog('ICloud getFileFromCloud: file not downloaded, trying to download', {
            fileName,
            attempts,
            timeout,
        });
        await CloudStorage.triggerSync(fileName, ICLOUD_SCOPE);
        for (let i = 0; i < attempts; i++) {
            const isDownloaded = await isFileDownloaded(fileName, fileList);
            if (isDownloaded) {
                result.exists = true;
                result.downloaded = true;
                if (withData) {
                    result.data = await CloudStorage.readFile(extractFilenameFromCloudFileName(fileName), ICLOUD_SCOPE);
                }
                return result;
            }
            await delay(timeout);
        }
    }
    toLog('ICloud getFileFromCloud: file still not downloaded after attempts', {
        fileName,
        attempts,
        timeout,
    });
    return result;

};

/**
 @deprecated
 * Check the status of a file in iCloud.
  * This function checks if a file with the given filename exists in iCloud.
  * It retrieves the list of files from iCloud and checks if the file name exists in the list.
  * If the file name is a cloud file name, it extracts the actual file name before
 */
export const checkFileStatus = async (filename, fileList = []) => {
    try {
        const fn = filename?.split?.('/')
            ?.pop
            ?.() || filename;

        const files = fileList?.length || await getFileList();
        const isDownloaded = files.find((f) => f === fn || f?.endsWith(fn));
        toLog('ICloud checkFileStatus', {
            files,
            fn,
            isDownloaded,
        });
        if (isDownloaded) {
            return {
                exists: true,
                isDownloaded: true,
                actualName: isDownloaded,
            };
        }
        const file = files.find((f) => f === fn || f?.endsWith(`${fn}.icloud`));
        toLog('ICloud checkFileStatus file for iCloud', {
            files,
            file,
            fn,
        });

        if (file) {
            return {
                exists: true,
                isDownloaded: false,
                actualName: file,
            };
        }

        return {
            exists: false,
        };
    } catch (error) {
        toLog('Error checking file status:', error);
        return {
            exists: false,
        };
    }
};

/**
 * @deprecated
 * Wait for a file to be downloaded in iCloud.
 * @param filename
 * @param maxRetries
 * @param ms
 * @returns {Promise<{exists: boolean, isDownloaded: boolean, actualName: null}|{exists: boolean, isDownloaded: boolean, actualName: *}|{exists: boolean, isDownloaded: boolean, actualName: *}|{exists: boolean}|{exists: boolean}|{isDownloaded}|{exists: boolean, isDownloaded: boolean, actualName: *}|{exists: boolean, isDownloaded: boolean, actualName: *}|{exists: boolean}|{exists: boolean}>}
 */
export const waitForFileSync = async (filename, maxRetries = 5, ms = 2000) => {
    const s = await checkFileStatus(filename);

    if (s.exists && !s.isDownloaded && s.actualName?.endsWith('.icloud')) {
        toLog('Start download file from iCloud', {
            filename,
            s,
        });
        await CloudStorage.triggerSync(s.actualName, ICLOUD_SCOPE);
    }

    for (let i = 0; i < maxRetries; i++) {
        const status = await checkFileStatus(filename);

        if (status.isDownloaded) {
            return status;
        }

        if (status.exists) {
            await delay(ms);
        } else {
            return status;
        }
    }

    return {
        exists: false,
        isDownloaded: false,
        actualName: null,
    };
};

/**
 * Get iCloud file list with sizes.
 * This function retrieves the list of files from iCloud and their sizes.
 * If the list is provided, it will use that list, otherwise it will read the files
 * from the iCloud AppData scope.
 * It returns a promise that resolves to an array of file objects with their sizes.
 * Each file object contains the file name, size, and other file statistics.
 * If an error occurs while reading the file list, it logs the error and returns an empty array.
 * @param list
 * @returns {Promise<[{file: string, size: number, isFile: function, isDirectory: function, birthtimeMs: number, mtimeMs: number}]>}
 */
export const getICloudFileListWithSizes = async (list) => {
    try {
        const files = list ?? await getFileList();
        const fileStats = await Promise.allSettled(
            files.map(async (file) => {
                return {
                    ...(await CloudStorage.stat(file, ICLOUD_SCOPE)),
                    file,
                };
            }),
        );
        return fileStats
            ?.filter(({
                status,
                value,
            }) => status === 'fulfilled' && value?.isFile?.())
            ?.map(({ value }) => value);
    } catch (e) {
        toLog('Error reading iCloud file list', e);
        return [];
    }
};
